<template>
  <WdsStack gap-custom="40px" class="tw-w-full">
    <WdsStack class="tw-w-3/5" direction-column gap-lg>
      <HireContractSummary :hire-id="hireId" />
      <FeatureFlagged name="hire-page-overview-tab-employment-info">
        <HireEmploymentDetailsSummary v-if="hasEmployment" :hire-id="hireId" />
      </FeatureFlagged>
      <HireStaffingAgencySummary v-if="isCompany || isRecruiter" :hire-id="hireId" />
      <HireMilestones v-if="hasMilestones" :hire-id="hireId" />
      <HireInternalFields v-if="viewingAsClient" :hire-id="hireId" />
      <HireWorkerCustomFields v-else :hire-id="hireId" />
    </WdsStack>
    <WdsStack class="tw-w-2/5 tw-h-max tw-rounded" direction-column gap-md>
      <HireStatusWorkflow :hire-id="hireId" />
      <HireWorkerPaymentWidget v-if="shouldShowPaymentWidget" :hire-id="hireId" />
      <HireDocuments :hire-id="hireId" />
      <HireJobRequirements :hire-id="hireId" />

      <TemporaryHireAlerts v-if="false" :hire-id="hireId" />
    </WdsStack>
  </WdsStack>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsStack } from '@worksome/wds-components'
import HireStaffingAgencySummary from './HireStaffingAgencySummary.vue'
import HireEmploymentDetailsSummary from './HireEmploymentDetailsSummary.vue'
import HireMilestones from './HireMilestones.vue'
import HireWorkerPaymentWidget from './HireWorkerPaymentWidget.vue'
import TemporaryHireAlerts from './HirePageCompliance/TemporaryHireAlerts.vue'
import { graphql, useFragment } from '@/gql'
import { HireActiveStatus } from '@/gql/graphql'
import HireJobRequirements from '@/components/Hire/HireJobRequirements.vue'
import HireContractSummary from '@/components/Hire/HireContractSummary.vue'
import HireInternalFields from '@/components/Hire/HireInternalFields.vue'
import HireWorkerCustomFields from '@/components/Hire/HireWorkerCustomFields.vue'
import HireDocuments from '@/components/Hire/HireDocuments.vue'
import HireStatusWorkflow from '@/components/Hire/HirePageCompliance/HireComplianceStatusWidget.vue'
import FeatureFlagged from '@/components/FeatureFlagged.vue'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

import { useHireRecruiter } from '@/components/Hire/composables/useHireRecruiter'

const props = defineProps<{
  hireId: string
}>()

const { isCompany, hasWorkerCapabilities, isRecruiter, isWorker } = useAccountCapabilities()
const { viewerIsSupplier } = useHireRecruiter(props.hireId)
const viewingAsClient = computed(() => isCompany.value && !viewerIsSupplier.value)

const HirePageOverviewFragment = graphql(`
  fragment HirePageOverviewType on Hire {
    id
    activeStatus
    hasMilestones
    hasEmployment
  }
`)

const { result } = useQuery(
  graphql(`
    query HirePageOverviewData($id: ID!) {
      hire(id: $id) {
        ...HirePageOverviewType
      }
    }
  `),
  () => ({
    id: props.hireId,
  }),
  {
    clientId: 'publicApi',
  },
)

const hire = computed(() => useFragment(HirePageOverviewFragment, result.value?.hire))

const hasMilestones = computed(() => hire.value?.hasMilestones ?? false)
const hasEmployment = computed(() => hire.value?.hasEmployment ?? false)

const viewingAsSupplier = computed(() => viewerIsSupplier.value || isRecruiter.value)

const acceptedStatuses: HireActiveStatus[] = [
  HireActiveStatus.Ready,
  HireActiveStatus.Active,
  HireActiveStatus.Ended,
  HireActiveStatus.Terminated,
]

const shouldShowPaymentWidget = computed(
  () =>
    (viewingAsSupplier.value || isWorker.value) &&
    hire.value?.activeStatus !== undefined &&
    acceptedStatuses.includes(hire.value.activeStatus),
)
</script>

<style lang="scss"></style>
